// ----- phase-small-findings-batch-1 (item 1) -----
//
// The New/Edit template form used to take a template's variables as a raw
// JSON array typed into a textarea. Two problems, both of which this module
// exists to close:
//
//   1. A non-developer had to hand-author JSON — a stray comma silently
//      became "Variables is not valid JSON" with nothing to act on.
//   2. NOTHING checked the variable list against the body. A template could
//      declare `sponsorName` and never use it, or use `{{sponsorAddress}}`
//      and never declare it, and both saved clean. The mismatch only showed
//      up later as a blank in a generated document.
//
// The placeholder syntaxes recognised here MIRROR the server's substitution
// rules in `src/lib/templates/sampleValues.ts` — deliberately, so that what
// the form warns about is exactly what the generator will substitute. Both
// syntaxes are live in seeded templates: `{{name}}` (the majority, and what
// `templateRender.ts` and TemplateGenerationService assume) and `{name}` (the
// meeting-request seeds, 68 placeholders). If the server's rules change,
// change them here in the same commit — the unit test names both files.

/** `{{name}}` — the syntax TemplateGenerationService and templateRender use. */
const DOUBLE_BRACE = /\{\{\s*([\w.-]+)\s*\}\}/g;

/** `{name}` — the meeting-request seeds' syntax. Strict, as on the server. */
const SINGLE_BRACE = /\{([A-Za-z][\w.-]*)\}/g;

/**
 * Every placeholder name that appears in a template body, in first-appearance
 * order, de-duplicated. Double braces are matched (and removed) first so
 * `{{x}}` never degrades into a bogus `{x}` hit on the single-brace pass —
 * the same ordering the server uses.
 */
export function extractBodyPlaceholders(body: string): string[] {
  const found: string[] = [];
  const seen = new Set<string>();
  const push = (name: string): void => {
    if (seen.has(name)) return;
    seen.add(name);
    found.push(name);
  };
  const withoutDouble = body.replace(DOUBLE_BRACE, (_m, name: string) => {
    push(name);
    return ' ';
  });
  for (const m of withoutDouble.matchAll(SINGLE_BRACE)) push(m[1] as string);
  return found;
}

export interface VariableMismatch {
  /** Used in the body, but not declared in the variables list. */
  readonly undeclared: string[];
  /** Declared in the variables list, but never used in the body. */
  readonly unused: string[];
}

/**
 * Compare a body against its declared variable names — BOTH directions.
 *
 * Matching is case-sensitive on purpose: substitution at generation time is
 * case-sensitive, so `{{SponsorName}}` genuinely does not resolve a variable
 * declared as `sponsorName`, and reporting it as a match would hide a real
 * defect. Blank/whitespace-only declared names are ignored (they are rows the
 * author has not filled in yet, not offenders).
 */
export function analyzeTemplateVariables(
  body: string,
  declaredNames: ReadonlyArray<string>,
): VariableMismatch {
  const used = extractBodyPlaceholders(body);
  const declared = declaredNames.map((n) => n.trim()).filter((n) => n !== '');
  const declaredSet = new Set(declared);
  const usedSet = new Set(used);
  return {
    undeclared: used.filter((n) => !declaredSet.has(n)),
    unused: [...new Set(declared)].filter((n) => !usedSet.has(n)),
  };
}

/** True when there is nothing to warn about. */
export function isVariablesConsistent(m: VariableMismatch): boolean {
  return m.undeclared.length === 0 && m.unused.length === 0;
}

/**
 * The warning sentences shown under the variables list. Each one NAMES the
 * offenders — a warning that says "there is a mismatch" without saying which
 * name is at fault is not actionable. Returns [] when consistent.
 *
 * These are warnings, never blocks: a template may legitimately be saved
 * mid-edit, and the author is the one who decides whether the mismatch is
 * intentional.
 */
export function variableWarnings(m: VariableMismatch): string[] {
  const out: string[] = [];
  if (m.undeclared.length > 0) {
    out.push(
      `Used in the body but not declared: ${m.undeclared.join(', ')}. ` +
        `Unless you add ${m.undeclared.length === 1 ? 'it' : 'them'}, ` +
        `${m.undeclared.length === 1 ? 'this placeholder' : 'these placeholders'} ` +
        `will have no value to substitute.`,
    );
  }
  if (m.unused.length > 0) {
    out.push(
      `Declared but never used in the body: ${m.unused.join(', ')}. ` +
        `${m.unused.length === 1 ? 'It' : 'They'} will be collected and then ignored.`,
    );
  }
  return out;
}
